import React, { createContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import routes from "../../routes";

export const UPDATE_STEP = "UPDATE_STEP";
export const SHOW_PROGRESS_BAR = "SHOW_PROGRESS_BAR";
export const CURRENT_STEP = "CURRENT_STEP";
export const SET_STEP = "SET_STEP";

export const INACTIVE = "inActive";
export const ACTIVE = "active";
export const COMPLETED = "completed";

export const SPSTEPS = [
  {
    name: "Basic info",
    substeps: [routes.applicantInfo, routes.basicInformation, routes.borrowAmount],
    totalSteps: 3,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Company info",
    substeps: [routes.companyInfo],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Stakeholders",
    substeps: [routes.stakeholdersInfo],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Business info",
    substeps: [routes.finalQuestions],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Documents",
    substeps: [routes.uploadDocuments],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Checks",
    substeps: [routes.AECBcheck],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  }
];

export const PESTEPS = [
  {
    name: "Company info",
    substeps: [routes.companyInfoPE],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Stakeholders",
    substeps: [routes.stakeholdersInfoPE],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Select Services",
    substeps: [routes.selectServices],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Documents",
    substeps: [routes.uploadDocumentsPE],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  },
  {
    name: "Submit",
    substeps: [routes.SubmitApplication],
    totalSteps: 1,
    status: INACTIVE,
    value: null,
    subStepIndex: 0
  }
];

export const STEP_TYPE = {
  SP: SPSTEPS,
  PE: PESTEPS
};

const ProgressBarContext = createContext();

export const initialState = {
  steps: SPSTEPS,
  showProgressBar: -1,
  currentStepName: null
};

const getStepsBasedOnRoute = currentRoute => {
  if (
    currentRoute?.includes("/post-eligibility/") ||
    currentRoute?.includes("SubmitApplication") ||
    currentRoute?.includes("SelectServices")
  ) {
    return STEP_TYPE.PE;
  }
  return STEP_TYPE.SP;
};

const updateSteps = newSteps => {
  return newSteps.map((step, index) => ({
    ...newSteps[index],
    ...step
  }));
};

export const reducer = (state, action) => {
  switch (action.type) {
    case SET_STEP: {
      const { currentRoute } = action.payload;

      const newSteps = getStepsBasedOnRoute(currentRoute);

      if (!newSteps) {
        return state;
      }

      return {
        ...state,
        steps: updateSteps(newSteps)
      };
    }

    case UPDATE_STEP: {
      const updatedSteps = state.steps.map((step, index) => {
        if (index === action.payload.index) {
          return {
            ...step,
            value: action.payload.value,
            status: action.payload.status,
            subStepIndex: action.payload.subStepIndex
          };
        }
        return step;
      });
      return {
        ...state,
        steps: [...updatedSteps]
      };
    }

    case SHOW_PROGRESS_BAR:
      return {
        ...state,
        showProgressBar: action.payload
      };

    case CURRENT_STEP:
      return {
        ...state,
        currentStepName: action.payload
      };

    default:
      return state;
  }
};

const ProgressBarProvider = ({ children }) => {
  const { pathname } = useLocation();

  const getInitialSteps = () => {
    if (pathname.includes("/post-eligibility/")) {
      return STEP_TYPE.PE;
    }
    return STEP_TYPE.SP;
  };

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    steps: getInitialSteps() // Dynamically set steps here
  });

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <ProgressBarContext.Provider value={contextValue}>{children}</ProgressBarContext.Provider>;
};

ProgressBarProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { ProgressBarProvider, ProgressBarContext };
